/** QuickPlan — Opérations sur un projet, partagées par l'éditeur et les outils de l'IA.
 * Toutes modifient `project` EN PLACE et lèvent une Error en français si la demande
 * est incohérente (le message remonte tel quel à l'utilisateur ou au modèle).
 *
 * Hiérarchie : une instance attachée (parentId) a sa pose exprimée dans le repère de
 * son parent (voir frames.js). Attacher / détacher conserve la position à l'écran.
 */
import { InstanceSchema, GroupSchema, newId, validateComponent } from './schema.js';
import { evalExpr, evalVec } from './expr.js';
import { resolveComponent, effectiveParams, rotMatrix, add, poseOnPort } from './resolve.js';
import { computeFrames, compose, invert, localFrame, descendants, parentFrameOf, portsInFrame, vector, IDENT } from './frames.js';

const round = (v) => Math.round(v * 1000) / 1000;

function fail(msg) { throw new Error(msg); }

/** Paramètres fournis → uniquement les clés connues, en nombres. */
function cleanParams(comp, given = {}) {
  const keys = new Set((comp.params ?? []).map((p) => p.key));
  const out = {};
  for (const [k, v] of Object.entries(given ?? {})) {
    if (!keys.has(k)) fail(`${comp.id} : paramètre « ${k} » inconnu (attendus : ${[...keys].join(', ') || 'aucun'})`);
    const n = Number(v);
    if (!Number.isFinite(n)) fail(`${comp.id} : valeur non numérique pour ${k}`);
    out[k] = n;
  }
  return effectiveParams(comp, out);
}

export function findInstance(project, id) {
  return project.instances.find((i) => i.id === id) ?? fail(`instance « ${id} » introuvable`);
}

function checkGroup(project, groupId) {
  if (groupId == null) return null;
  if (!project.groups.some((g) => g.id === groupId)) fail(`sous-ensemble « ${groupId} » introuvable`);
  return groupId;
}

/** Résolveur tolérant pour les repères (un composant cassé = pas d'articulation). */
export function resolverFor(lookup) {
  return (inst) => {
    const c = lookup(inst.componentId);
    if (!c) return null;
    try { return resolveComponent(c, inst.params); } catch { return null; }
  };
}

export const framesOf = (project, lookup) => computeFrames(project, resolverFor(lookup));

/** Ports monde d'une instance (hiérarchie et articulations comprises). */
export function instancePorts(project, lookup, inst) {
  const comp = lookup(inst.componentId) ?? fail(`composant « ${inst.componentId} » introuvable`);
  return portsInFrame(framesOf(project, lookup).get(inst.id), resolveComponent(comp, inst.params));
}

/** Ouverture bornée à la course de l'articulation (0 sans articulation). */
function clampOpen(comp, params, open) {
  if (!comp.joint || !open) return 0;
  const j = resolveComponent(comp, params).joint;
  return round(Math.max(j.min, Math.min(j.max, Number(open) || 0)));
}

/**
 * Ajoute une instance.
 *  parentId : pos/rot exprimées dans le repère du parent.
 *  attach = { to, port, myPort, parent } : pose port sur port ; parent = true attache
 *  en plus l'instance à la cible (elle la suivra).
 */
export function addInstance(project, lookup, spec) {
  const comp = lookup(spec.componentId) ?? fail(`composant « ${spec.componentId} » introuvable dans la bibliothèque`);
  const params = cleanParams(comp, spec.params);
  const rot = (spec.rot ?? [0, 0, 0]).map(Number);
  let pos = (spec.pos ?? [0, 0, 0]).map(Number);
  let parentId = spec.parentId ?? null;
  if (parentId) findInstance(project, parentId);

  if (spec.attach) {
    const target = findInstance(project, spec.attach.to);
    const tcomp = lookup(target.componentId) ?? fail(`composant « ${target.componentId} » introuvable`);
    const tport = resolveComponent(tcomp, target.params).ports.find((p) => p.id === spec.attach.port)
      ?? fail(`port « ${spec.attach.port} » introuvable sur ${target.name || target.id}`);
    const myPorts = resolveComponent(comp, params).ports;
    const mine = myPorts.find((p) => p.id === (spec.attach.myPort ?? myPorts[0]?.id))
      ?? fail(`port « ${spec.attach.myPort} » introuvable sur ${comp.id}`);
    const frames = framesOf(project, lookup);
    const tF = frames.get(target.id);
    if (spec.attach.parent) {
      parentId = target.id;
      pos = poseOnPort(rot, mine.pos, tport.pos); // directement dans le repère de la cible
    } else {
      const world = add(vector(tF, tport.pos), tF.t);
      const pF = parentId ? frames.get(parentId) : IDENT;
      pos = poseOnPort(rot, mine.pos, add(vector(invert(pF), world), invert(pF).t));
    }
  }

  const parent = parentId ? findInstance(project, parentId) : null;
  const inst = InstanceSchema.parse({
    id: newId('i'),
    componentId: comp.id,
    name: spec.name || undefined,
    params,
    pos: pos.map(round),
    rot: rot.map(round),
    groupId: checkGroup(project, spec.groupId ?? parent?.groupId ?? null),
    parentId,
    open: clampOpen(comp, params, spec.open),
    step: spec.step ?? null,
    color: spec.color,
    note: spec.note,
  });
  project.instances.push(inst);
  return inst;
}

/**
 * Attache `id` à `parentId` (null = détacher). keepWorld : la pièce ne bouge pas à
 * l'écran (sa pose est recalculée dans le nouveau repère).
 */
export function setParent(project, lookup, id, parentId, keepWorld = true) {
  const inst = findInstance(project, id);
  if (parentId === inst.parentId) return inst;
  if (parentId) {
    if (parentId === id) fail('une pièce ne peut pas être attachée à elle-même');
    findInstance(project, parentId);
    if (descendants(project, id).has(parentId)) fail('attache impossible : la cible est déjà portée par cette pièce');
  }
  if (keepWorld) {
    const frames = framesOf(project, lookup);
    const world = compose(parentFrameOf(frames, inst), localFrame(inst));
    const newParentF = parentId ? frames.get(parentId) : IDENT;
    const local = compose(invert(newParentF), world);
    inst.pos = local.t.map(round);
    inst.rot = eulerFromMatrix(local.R);
  }
  inst.parentId = parentId;
  if (parentId) {
    const g = findInstance(project, parentId).groupId;
    inst.groupId = g;
    for (const d of descendants(project, id)) findInstance(project, d).groupId = g;
  }
  return inst;
}

const PATCHABLE = ['name', 'pos', 'rot', 'groupId', 'hidden', 'color', 'step', 'note', 'componentId', 'open'];

/** Modifie une instance : params fusionnés, autres champs remplacés ; parentId passe
 *  par setParent (position conservée à l'écran, sauf si pos est fournie en même temps). */
export function updateInstance(project, lookup, id, patch) {
  const inst = findInstance(project, id);
  if (patch.parentId !== undefined && patch.parentId !== inst.parentId) {
    setParent(project, lookup, id, patch.parentId, patch.pos === undefined);
  }
  const next = { ...inst };
  for (const k of PATCHABLE) if (patch[k] !== undefined) next[k] = patch[k];
  if (patch.groupId !== undefined) checkGroup(project, patch.groupId);
  const comp = lookup(next.componentId) ?? fail(`composant « ${next.componentId} » introuvable`);
  const merged = { ...(next.componentId === inst.componentId ? inst.params : {}), ...(patch.params ?? {}) };
  // Changement de composant : on ne garde que les paramètres qu'il connaît.
  const known = new Set((comp.params ?? []).map((p) => p.key));
  next.params = cleanParams(comp, Object.fromEntries(Object.entries(merged).filter(([k]) => known.has(k) || patch.params?.[k] !== undefined)));
  if (next.pos) next.pos = next.pos.map((v) => round(Number(v)));
  if (next.rot) next.rot = next.rot.map((v) => round(Number(v)));
  next.open = clampOpen(comp, next.params, next.open);
  const parsed = InstanceSchema.parse(next);
  Object.assign(inst, parsed);
  if (patch.color === null) delete inst.color;
  // Un porteur qui change de sous-ensemble emmène ce qu'il porte.
  if (patch.groupId !== undefined) for (const d of descendants(project, id)) findInstance(project, d).groupId = inst.groupId;
  return inst;
}

/** Supprime des instances ET tout ce qu'elles portent. Rend le nombre supprimé. */
export function deleteInstances(project, ids) {
  const set = new Set(ids);
  for (const id of ids) for (const d of descendants(project, id)) set.add(d);
  const before = project.instances.length;
  project.instances = project.instances.filter((i) => !set.has(i.id));
  return before - project.instances.length;
}

/** Ne garde que les « têtes » d'une sélection (pas une pièce dont un ancêtre est déjà dedans). */
export function selectionRoots(project, ids) {
  const set = new Set(ids);
  const byId = new Map(project.instances.map((i) => [i.id, i]));
  return ids.filter((id) => {
    let p = byId.get(id)?.parentId;
    let guard = 0;
    while (p && guard++ < 100) {
      if (set.has(p)) return false;
      p = byId.get(p)?.parentId;
    }
    return byId.has(id);
  });
}

/** Duplique des instances avec ce qu'elles portent. `offset` est un décalage monde. */
export function duplicateInstances(project, ids, offset = [0, 0, 0], lookup = null) {
  const roots = selectionRoots(project, ids);
  const frames = lookup ? framesOf(project, lookup) : null;
  const out = [];
  for (const id of roots) {
    const tree = [id, ...descendants(project, id)];
    const map = new Map(tree.map((x) => [x, newId('i')]));
    for (const x of tree) {
      const src = findInstance(project, x);
      const copy = { ...JSON.parse(JSON.stringify(src)), id: map.get(x) };
      if (x === id) {
        // Décalage monde → repère du parent.
        const d = frames && src.parentId ? vector(invert(frames.get(src.parentId)), offset) : offset;
        copy.pos = add(src.pos, d).map(round);
      } else {
        copy.parentId = map.get(src.parentId);
      }
      project.instances.push(copy);
      if (x === id) out.push(copy);
    }
  }
  return out;
}

/** Déplacement monde d'une sélection (les pièces portées suivent leur porteur). */
export function moveInstances(project, ids, delta, lookup = null) {
  const frames = lookup ? framesOf(project, lookup) : null;
  for (const id of selectionRoots(project, ids)) {
    const inst = findInstance(project, id);
    const d = frames && inst.parentId ? vector(invert(frames.get(inst.parentId)), delta) : delta;
    inst.pos = add(inst.pos, d).map(round);
  }
}

/** Composant porteur de l'articulation la plus proche (soi-même ou un ancêtre). */
export function jointOwner(project, lookup, id) {
  const byId = new Map(project.instances.map((i) => [i.id, i]));
  let cur = byId.get(id);
  let guard = 0;
  while (cur && guard++ < 100) {
    if (lookup(cur.componentId)?.joint) return cur;
    cur = cur.parentId ? byId.get(cur.parentId) : null;
  }
  return null;
}

/** Ouvre (au maximum) ou ferme l'articulation d'une instance. */
export function toggleOpen(project, lookup, id) {
  const owner = jointOwner(project, lookup, id);
  if (!owner) return null;
  const j = resolveComponent(lookup(owner.componentId), owner.params).joint;
  owner.open = owner.open ? 0 : round(j.max);
  return owner;
}

/** Tout ouvrir (au maximum) ou tout fermer. */
export function setAllOpen(project, lookup, open) {
  let n = 0;
  for (const inst of project.instances) {
    const comp = lookup(inst.componentId);
    if (!comp?.joint) continue;
    inst.open = open ? round(resolveComponent(comp, inst.params).joint.max) : 0;
    n++;
  }
  return n;
}

// ── Sous-ensembles ──────────────────────────────────────────────

export function addGroup(project, name, parentId = null) {
  const g = GroupSchema.parse({ id: newId('g'), name: name || 'Sous-ensemble', parentId: checkGroup(project, parentId) });
  project.groups.push(g);
  return g;
}

/** Ids du groupe et de tous ses descendants. */
export function groupTree(project, groupId) {
  const out = new Set([groupId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const g of project.groups) {
      if (g.parentId && out.has(g.parentId) && !out.has(g.id)) { out.add(g.id); grew = true; }
    }
  }
  return out;
}

/** Supprime un sous-ensemble. withContents : supprime aussi son contenu ;
 *  sinon groupes et instances enfants remontent au parent. */
export function deleteGroup(project, id, withContents = false) {
  const g = project.groups.find((x) => x.id === id) ?? fail(`sous-ensemble « ${id} » introuvable`);
  if (withContents) {
    const tree = groupTree(project, id);
    deleteInstances(project, project.instances.filter((i) => tree.has(i.groupId)).map((i) => i.id));
    project.groups = project.groups.filter((x) => !tree.has(x.id));
    return;
  }
  for (const x of project.groups) if (x.parentId === id) x.parentId = g.parentId;
  for (const i of project.instances) if (i.groupId === id) i.groupId = g.parentId;
  project.groups = project.groups.filter((x) => x.id !== id);
}

/** Déplace un groupe sous un autre (anti-cycle). */
export function setGroupParent(project, id, parentId) {
  const g = project.groups.find((x) => x.id === id) ?? fail(`sous-ensemble « ${id} » introuvable`);
  if (parentId && groupTree(project, id).has(parentId)) fail('un sous-ensemble ne peut pas être rangé dans lui-même');
  g.parentId = checkGroup(project, parentId);
}

// ── Rotations composées ─────────────────────────────────────────

/** Matrice → Euler XYZ (degrés), même convention que three.js. */
export function eulerFromMatrix(m) {
  const DEG = 180 / Math.PI;
  const m13 = Math.max(-1, Math.min(1, m[0][2]));
  const y = Math.asin(m13);
  let x;
  let z;
  if (Math.abs(m13) < 0.9999999) {
    x = Math.atan2(-m[1][2], m[2][2]);
    z = Math.atan2(-m[0][1], m[0][0]);
  } else {
    x = Math.atan2(m[2][1], m[1][1]);
    z = 0;
  }
  return [x, y, z].map((a) => round(a * DEG) + 0); // + 0 : pas de -0
}

function mul(a, b) {
  return a.map((row) => [0, 1, 2].map((j) => row[0] * b[0][j] + row[1] * b[1][j] + row[2] * b[2][j]));
}

/** Rotation composée : d'abord `inner`, puis `outer` (R = Router · Rinner). */
export function composeRot(outer, inner) {
  return eulerFromMatrix(mul(rotMatrix(outer), rotMatrix(inner)));
}

/** Rotation (Euler XYZ, degrés) qui amène l'axe +Z local sur la direction `n`. */
export function rotZTo(n) {
  const len = Math.hypot(...n) || 1;
  const z = n.map((v) => v / len);
  const ref = Math.abs(z[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
  const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
  let x = cross(ref, z);
  const xl = Math.hypot(...x);
  x = x.map((v) => v / xl);
  const y = cross(z, x);
  return eulerFromMatrix([[x[0], y[0], z[0]], [x[1], y[1], z[1]], [x[2], y[2], z[2]]]);
}

// ── Assemblages types ───────────────────────────────────────────

/** Insère un assemblage type dans un nouveau sous-ensemble. Les instances à `parent`
 *  sont attachées (pose relative au parent, même indice de répétition). */
export function insertTemplate(project, lookup, tpl, given = {}, opts = {}) {
  const vars = effectiveParams(tpl, given);
  const tplRot = (opts.rot ?? [0, 0, 0]).map(Number);
  const tplPos = (opts.pos ?? [0, 0, 0]).map(Number);
  const R = rotMatrix(tplRot);
  const group = addGroup(project, opts.groupName || tpl.name, opts.parentId ?? null);
  const created = [];
  const byKey = new Map(); // clé → [instances par indice]
  try {
    for (const ti of tpl.instances) {
      const n = ti.repeat ? Math.max(0, Math.floor(evalExpr(ti.repeat.count, vars))) : 1;
      for (let i = 0; i < n; i++) {
        const v = ti.repeat ? { ...vars, [ti.repeat.var ?? 'i']: i } : vars;
        const params = Object.fromEntries(Object.entries(ti.params ?? {}).map(([k, e]) => [k, evalExpr(e, v)]));
        const local = evalVec(ti.pos, v);
        const lrot = evalVec(ti.rot ?? [0, 0, 0], v);
        let parent = null;
        if (ti.parent) {
          const list = byKey.get(ti.parent) ?? fail(`gabarit ${tpl.id} : parent « ${ti.parent} » inconnu (il doit être déclaré avant)`);
          parent = list[i] ?? list[0];
        }
        const inst = addInstance(project, lookup, {
          componentId: ti.componentId,
          name: ti.name,
          params,
          pos: parent ? local : add(rotate(R, local), tplPos),
          rot: parent ? lrot : composeRot(tplRot, lrot),
          parentId: parent?.id ?? null,
          groupId: group.id,
          step: ti.step ?? null,
        });
        created.push(inst);
        if (ti.key) {
          if (!byKey.has(ti.key)) byKey.set(ti.key, []);
          byKey.get(ti.key).push(inst);
        }
      }
    }
  } catch (e) {
    // Tout ou rien : un gabarit à moitié posé serait pire qu'une erreur.
    const ids = new Set(created.map((c) => c.id));
    project.instances = project.instances.filter((x) => !ids.has(x.id));
    project.groups = project.groups.filter((g) => g.id !== group.id);
    throw e;
  }
  return { group, instances: created };
}

const rotate = (R, v) => [0, 1, 2].map((k) => R[k][0] * v[0] + R[k][1] * v[1] + R[k][2] * v[2]);

// ── Composants du projet ────────────────────────────────────────

/** Crée ou remplace un composant propre au projet (validé, et résolu à blanc). */
export function upsertComponent(project, raw) {
  const check = validateComponent(raw);
  if (!check.ok) fail(`composant invalide : ${check.errors.join(' ; ')}`);
  resolveComponent(check.component, {}); // lève si une expression est fausse
  const i = project.components.findIndex((c) => c.id === check.component.id);
  if (i >= 0) project.components[i] = check.component;
  else project.components.push(check.component);
  return check.component;
}
